import { useState, useCallback, useMemo, useEffect } from 'react';

import { getEORIValidation } from '../../../api/util';

const useErrors = (
  state,
  referenceData,
  toRunInitialErrorCheck,
  setToRunInitialErrorCheck,
  setGoToNextStepFunc,
  setActiveStep,
  errorsChecked,
  setErrorsChecked,
  consignmentsVisited,
  currentConsignmentErrors,
  setCurrentConsignmentErrors,
  currentConsignmentHMRCErrors,
  setCurrentConsignmentHMRCErrors,
  currentConsignment,
  data
) => {
  const [errors, setErrors] = useState({});
  const [errorsHMRC, setErrorsHMRC] = useState({});

  const [errorsLocal, setErrorsLocal] = useState({});

  const [initialErrorCheckRun, setInitialErrorCheckRun] = useState(false);

  const [isAccompaniedLoaded, setIsAccompaniedLoaded] = useState(false);

  //Used in error check to determine if the vehicle is accompanied
  const isAccompanied = useMemo(() => {
    if (Object.keys(referenceData).length > 0)
      setTimeout(() => {
        setIsAccompaniedLoaded(true);
      }, 100);
    const value = referenceData?.modeOfTransport
      ?.find((r) => parseInt(r.key) === parseInt(data?.TransportAndJourney?.Vehicle?.ModeOfTransport))
      ?.value?.toLowerCase?.();
    return value?.includes?.('off accompanied') || value?.includes?.('euro shuttle');
  }, [referenceData, data]);

  //Error functions used in error definitions
  const errorFunctions = useMemo(
    () => ({
      required: (val) => !!val,
      selected: (val) => !!val && val !== -1,
      dateBeforeArrival: (val) => {
        if (!val) return false;
        return !val.isAfter(data.TransportAndJourney.arrivalDateTime);
      },
      dateAfterDeparture: (d, departure) => {
        if (!d) return false;
        return !d.isBefore(departure);
      },
      transportDocumentsPresent: (rows) => rows?.length && rows.length > 0,
      trailerOrContainerNo: (trailerNo) => {
        return !(
          (!trailerNo || trailerNo?.trim?.()?.length === 0) &&
          (!data?.TransportAndJourney?.Vehicle?.containerNo ||
            data?.TransportAndJourney?.Vehicle?.containerNo?.trim?.()?.length === 0)
        );
      },
      validVehicleOrContainer: (val) => {
        const trimmedVal = val?.trim?.()?.toUpperCase?.();
        return !(!trimmedVal || trimmedVal?.length === 0 || ['TEST', '.', 'TRUCK', 'TRAILER'].includes(trimmedVal));
      },
      carrierIfAccompanied: (v) => {
        return !((!v || v === '') && !isAccompanied);
      },
      allOrNoneLoading: () => {
        const country = data?.TransportAndJourney?.PlaceOfLoading?.country;
        const countrySelected = country && country !== -1 && country?.trim?.() !== '';
        const placeName =
          data?.TransportAndJourney?.PlaceOfLoading?.placeName &&
          data?.TransportAndJourney?.PlaceOfLoading?.placeName?.trim?.()?.length > 0;
        const postcode =
          data?.TransportAndJourney?.PlaceOfLoading?.postcode &&
          data?.TransportAndJourney?.PlaceOfLoading?.postcode?.trim?.()?.length > 0;
        if (countrySelected && placeName && postcode) return true;
        return !!(!countrySelected && !placeName && !postcode);
      },
      allOrNoneUnloading: () => {
        const country = data?.TransportAndJourney?.PlaceOfUnloading?.country;
        const countrySelected = country && country !== -1 && country?.trim?.() !== '';
        const placeName =
          data?.TransportAndJourney?.PlaceOfUnloading?.placeName &&
          data?.TransportAndJourney?.PlaceOfUnloading?.placeName?.trim?.()?.length > 0;
        const postcode =
          data?.TransportAndJourney?.PlaceOfUnloading?.postcode &&
          data?.TransportAndJourney?.PlaceOfUnloading?.postcode?.trim?.()?.length > 0;
        if (countrySelected && placeName && postcode) return true;
        return !!(!countrySelected && !placeName && !postcode);
      },
      allOrNoneLoadingConsignment: () => {
        const countryTJ = data?.TransportAndJourney?.PlaceOfLoading?.country;
        const countrySelectedTJ = countryTJ && countryTJ !== -1 && countryTJ?.trim?.() !== '';
        const placeNameTJ =
          data?.TransportAndJourney?.PlaceOfLoading?.placeName &&
          data?.TransportAndJourney?.PlaceOfLoading?.placeName?.trim?.()?.length > 0;
        const postcodeTJ =
          data?.TransportAndJourney?.PlaceOfLoading?.postcode &&
          data?.TransportAndJourney?.PlaceOfLoading?.postcode?.trim?.()?.length > 0;
        if (countrySelectedTJ || placeNameTJ || postcodeTJ) {
          return true;
        }

        const country = currentConsignment?.PlaceOfLoading?.country;
        const countrySelected = country && country !== -1 && country?.trim?.() !== '';
        const placeName =
          currentConsignment?.PlaceOfLoading?.placeName &&
          currentConsignment?.PlaceOfLoading?.placeName?.trim?.()?.length > 0;
        const postcode =
          currentConsignment?.PlaceOfLoading?.postcode &&
          currentConsignment?.PlaceOfLoading?.postcode?.trim?.()?.length > 0;
        if (countrySelected && placeName && postcode) {
          return true;
        }
        return !!(!countrySelected && !placeName && !postcode);
      },
      allOrNoneUnloadingConsignment: () => {
        const countryTJ = data?.TransportAndJourney?.PlaceOfUnloading?.country;
        const countrySelectedTJ = countryTJ && countryTJ !== -1 && countryTJ?.trim?.() !== '';
        const placeNameTJ =
          data?.TransportAndJourney?.PlaceOfUnloading?.placeName &&
          data?.TransportAndJourney?.PlaceOfUnloading?.placeName?.trim?.()?.length > 0;
        const postcodeTJ =
          data?.TransportAndJourney?.PlaceOfUnloading?.postcode &&
          data?.TransportAndJourney?.PlaceOfUnloading?.postcode?.trim?.()?.length > 0;
        if (countrySelectedTJ || placeNameTJ || postcodeTJ) return true;

        const country = currentConsignment?.PlaceOfUnloading?.country;
        const countrySelected = country && country !== -1 && country?.trim?.() !== '';
        const placeName =
          currentConsignment?.PlaceOfUnloading?.placeName &&
          currentConsignment?.PlaceOfUnloading?.placeName?.trim?.()?.length > 0;
        const postcode =
          currentConsignment?.PlaceOfUnloading?.postcode &&
          currentConsignment?.PlaceOfUnloading?.postcode?.trim?.()?.length > 0;
        if (countrySelected && placeName && postcode) return true;
        return !!(!countrySelected && !placeName && !postcode);
      },
      validGBEORIOrBlank: async (eori) => {
        if (!eori || eori.trim().length === 0) return true;
        const res = await getEORIValidation(eori, 'true');
        return res.statusDescr !== 'In Valid';
      },
      validGBEORI: async (eori) => {
        const res = await getEORIValidation(eori, 'true');
        return res.statusDescr !== 'In Valid';
      },
      validDangerousGoodsCodeOrBlank: (code) => {
        // dangerous goods code are always 4 digits ranging from 0001 to 9999
        if (!code || code.toString().trim().length === 0) return true;
        if (code === '0000') return false;

        const regex = /^\d{4}$/;
        return regex.test(code);
      },
      positiveNumber: (val) => {
        const newValue = Math.abs(parseInt(val));
        return val ? newValue.toString() === val.toString() : true;
      },
      positiveNumberAndRequired: (val) => {
        const newValue = Math.abs(parseInt(val));
        return val ? newValue.toString() === val.toString() && !!val : false;
      },
      wholeNumber: (val) => {
        return Number.isInteger(parseFloat(val));
      },
      positiveFloatAndRequired: (val) => {
        const newValue = parseFloat(val);
        return val !== '' && !isNaN(newValue) && newValue >= 0;
      },
      piecesRequired: (numPieces, line) => {
        if (!referenceData?.packagesWithPiecesRequired) return false;
        const packageType = line?.packageType;
        const piecesFieldRequired = referenceData.packagesWithPiecesRequired.includes(packageType.toString());

        const newValue = Math.abs(parseInt(numPieces));
        const positiveNumberAndRequired = numPieces
          ? newValue.toString() === numPieces.toString() && !!numPieces
          : false;

        // if pieces required, or has been entered by user, we should validate the value
        return !((piecesFieldRequired || numPieces) && !positiveNumberAndRequired);
      },
      noSpecialCharacters: (val) => {
        if (val?.trim() === '') {
          return true;
        }
        return /^[A-Za-z0-9]+$/.test(val);
      },
    }),
    [data, referenceData, currentConsignment, isAccompanied]
  );

  //Error definitions for each field, and modal. Special logic for consignment lines.
  const errorDefinitions = useMemo(
    () => ({
      TransportAndJourney: [
        { id: 'jobReference', func: errorFunctions.required, errorText: 'Job Reference is required.' },
        {
          id: 'Vehicle',
          type: 'modal',
          errorText: 'There are errors with the vehicle.',
          children: [
            { id: 'ModeOfTransport', func: errorFunctions.selected, errorText: 'Mode of Transport is required.' },
            {
              id: 'vehicleID',
              func: errorFunctions.validVehicleOrContainer,
              errorText:
                "Vehicle ID cannot be TEST, TRUCK, TRAILER, blank, or contain only a full stop ('.'). Please enter a valid Vehicle ID.",
            },
            { id: 'vehicleID', func: errorFunctions.selected, errorText: 'Vehicle ID is required.' },
            { id: 'Nationality', func: errorFunctions.selected, errorText: 'Nationality is required.' },
            {
              id: 'trailerNo',
              func: errorFunctions.validVehicleOrContainer,
              errorText:
                "Trailer/Container Number cannot be TEST, TRUCK, TRAILER, blank, or contain only a full stop ('.'). Please enter a valid value.",
            },
            {
              id: 'trailerNo',
              func: errorFunctions.trailerOrContainerNo,
              errorText: 'Trailer or container number is required.',
            },
          ],
        },
        {
          id: 'carrierFerryOperator',
          func: errorFunctions.validGBEORIOrBlank,
          isAsync: true,
          errorText: 'Must be valid GB EORI number.',
        },
        {
          id: 'carrierFerryOperator',
          func: errorFunctions.carrierIfAccompanied,
          errorText: 'Carrier/Ferry operator required if unaccompanied vehicle.',
        },
        { id: 'portOfDeparture', func: errorFunctions.selected, errorText: 'Port of Departure is required.' },
        {
          id: 'departure',
          type: 'date',
          func: errorFunctions.dateBeforeArrival,
          errorText: 'Date must be before arrival date.',
        },
        { id: 'portOfArrival', func: errorFunctions.selected, errorText: 'Port of Arrival is required.' },
        {
          id: 'arrival',
          type: 'dateTime',
          func: errorFunctions.dateAfterDeparture,
          errorText: 'Date must be after departure date.',
        },
        {
          id: 'PlaceOfLoading',
          type: 'modal',
          errorText: 'There are errors with the place of loading.',
          children: [
            {
              id: 'country',
              func: errorFunctions.allOrNoneLoading,
              errorText: 'Either fill in all fields, or delete all data.',
            },
          ],
        },
        {
          id: 'PlaceOfUnloading',
          type: 'modal',
          errorText: 'There are errors with the place of unloading.',
          children: [
            {
              id: 'country',
              func: errorFunctions.allOrNoneUnloading,
              errorText: 'Either fill in all fields, or delete all data.',
            },
          ],
        },
        {
          id: 'Route',
          type: 'modal',
          errorText: 'There are errors with the route.',
          children: [
            { id: 'departureCountry', func: errorFunctions.selected, errorText: 'Departure country is required.' },
          ],
        },
      ],
      Consignments: [
        { id: 'consignmentReference', func: errorFunctions.required, errorText: 'Consignment Reference is required' },
        {
          id: 'Consignor',
          type: 'modal',
          errorText: 'There are errors with the consignor.',
          children: [
            { id: 'Country', func: errorFunctions.selected, errorText: 'Country is required.' },
            { id: 'companyName', func: errorFunctions.required, errorText: 'Company Name is required.' },
            { id: 'streetAddress', func: errorFunctions.required, errorText: 'Street Address is required.' },
            { id: 'townCity', func: errorFunctions.required, errorText: 'Town / City is required.' },
            { id: 'postCode', func: errorFunctions.required, errorText: 'Postcode is required.' },
          ],
        },
        {
          id: 'Consignee',
          type: 'modal',
          errorText: 'There are errors with the consignee.',
          children: [
            { id: 'EORI', func: errorFunctions.required, errorText: 'GB EORI or Trader ID is required.' },
            {
              id: 'EORI',
              func: errorFunctions.noSpecialCharacters,
              errorText: 'GB EORI or Trader ID must not contain special characters.',
            },
            { id: 'companyName', func: errorFunctions.required, errorText: 'Company Name is required.' },
            { id: 'streetAddress', func: errorFunctions.required, errorText: 'Street Address is required.' },
            { id: 'townCity', func: errorFunctions.required, errorText: 'Town / City is required.' },
            { id: 'postCode', func: errorFunctions.required, errorText: 'Postcode is required.' },
          ],
        },
        {
          id: 'PlaceOfLoading',
          type: 'modal',
          errorText: 'There are errors with the place of loading.',
          children: [
            {
              id: 'country',
              func: errorFunctions.allOrNoneLoadingConsignment,
              errorText: 'Either fill in all fields, or delete all data.',
            },
          ],
        },
        {
          id: 'PlaceOfUnloading',
          type: 'modal',
          errorText: 'There are errors with the place of unloading.',
          children: [
            {
              id: 'country',
              func: errorFunctions.allOrNoneUnloadingConsignment,
              errorText: 'Either fill in all fields, or delete all data.',
            },
          ],
        },
        {
          id: 'lines',
          type: 'consignmentLines',
          errorText: 'There are errors with the consignment lines.',
          errorTextLocal: 'There are errors with this consignment line.',
          children: [
            { id: 'goodsDescription', func: errorFunctions.required, errorText: 'Goods Description is required.' },
            { id: 'packageType', func: errorFunctions.selected, errorText: 'Type Of Packaging is required.' },
            { id: 'packagingMarks', func: errorFunctions.required, errorText: 'Packaging Marks is required.' },
            {
              id: 'numPackages',
              func: errorFunctions.positiveNumberAndRequired,
              errorText: 'Number Of Packages must be a number greater than or equal to zero.',
            },
            {
              id: 'numPackages',
              func: errorFunctions.wholeNumber,
              errorText: 'Number of Packages must be a whole number.',
            },
            {
              id: 'numPieces',
              func: errorFunctions.piecesRequired,
              errorText: 'Number of pieces is required for loose items and must be a number.',
            },
            {
              id: 'grossWeight',
              func: errorFunctions.positiveFloatAndRequired,
              errorText: 'Gross weight must be a number greater than or equal to zero.',
            },
            {
              id: 'dangerousGoods',
              func: errorFunctions.validDangerousGoodsCodeOrBlank,
              errorText: 'UN Dangerous Goods Description must be selected from the drop down or a 4 digit code.',
            },
          ],
        },
        { id: 'consignmentLinesPresent', errorText: 'Consignment must have at least 1 line' },
      ],
      AdditionalDetails: [
        { id: 'sealNumber', func: errorFunctions.required, errorText: 'Seal Number is required' },
        { id: 'transportCharges', func: errorFunctions.required, errorText: 'Transport Charges is required' },
        {
          id: 'TransportDocuments',
          type: 'modal',
          errorText: 'Errors with transport documents',
          children: [
            {
              id: 'rows',
              func: errorFunctions.transportDocumentsPresent,
              errorText: 'At least 1 Transport Document is required',
            },
          ],
        },
      ],
    }),
    [errorFunctions]
  );

  //Check whether to check errors at the start of the page load
  useEffect(() => {
    const check = async () => {
      if (state?.headerId && toRunInitialErrorCheck && data && !data.initialLoad && isAccompaniedLoaded) {
        runInitialErrorCheck();
        setToRunInitialErrorCheck(false);
      }
    };
    check();
  }, [toRunInitialErrorCheck, data, state, isAccompaniedLoaded]);

  //Check which pages to check, based on if they have data, and if so, check for errors.
  const runInitialErrorCheck = async () => {
    const TransportAndJourneyPresent =
      data['TransportAndJourney'] && Object.keys(data['TransportAndJourney']).length > 0;
    const ConsignmentsPresent = data['Consignments'] && data['Consignments'].length > 0;
    const AdditionalDetailsPresent = data['AdditionalDetails'] && Object.keys(data['AdditionalDetails']).length > 0;

    let TransportAndJourneyValidation = null;
    let TransportAndJourneyHMRC = null;
    let ConsignmentsValidation = null;
    let ConsignmentsHMRC = null;
    let AdditionalDetailsValidation = null;
    let AdditionalDetailsHMRC = null;

    if (TransportAndJourneyPresent || ConsignmentsPresent || AdditionalDetailsPresent) {
      const TransportAndJourney = await checkErrorsStandard('TransportAndJourney', true);
      TransportAndJourneyValidation = TransportAndJourney._errors;
      TransportAndJourneyHMRC = TransportAndJourney._hmrcErrors;
    }

    if (ConsignmentsPresent || AdditionalDetailsPresent) {
      const consignmentErrors = [];
      for (let i = 0; i < data?.Consignments?.length; i++) {
        const consignment = data?.Consignments[i];
        const res = await checkErrorsConsignment(consignment);
        consignmentErrors.push(res);
      }
      ConsignmentsValidation = consignmentErrors.map((c) => c._errors);
      ConsignmentsHMRC = consignmentErrors.map((c) => c._errorsHMRC);
    }

    if (AdditionalDetailsPresent) {
      const AdditionalDetails = await checkErrorsStandard('AdditionalDetails', true);
      AdditionalDetailsValidation = AdditionalDetails._errors;
      AdditionalDetailsHMRC = AdditionalDetails._hmrcErrors;
    }

    setErrors({
      TransportAndJourney: TransportAndJourneyValidation,
      Consignments: ConsignmentsValidation,
      AdditionalDetails: AdditionalDetailsValidation,
    });
    setErrorsHMRC({
      Consignments: ConsignmentsHMRC,
      TransportAndJourney: TransportAndJourneyHMRC,
      AdditionalDetails: AdditionalDetailsHMRC,
    });
    setInitialErrorCheckRun(true);
  };

  //================================================================================================
  //========================Main error checking functions=========================================
  //================================================================================================
  //Please change the error definitions above instead of changing this code.
  //Please consult Ben before changing the below code.

  //Helper function to find HMRC error for a specific consignment line
  const findMatchingConsignmentError = (consignmentId, itemId, columnName) => {
    const matchingConsignment = data.ErrorCodeColumnMapping?.Consignments?.find?.((c) => c.id === consignmentId);
    const matchingLine = matchingConsignment?.lines?.find?.((l) => l.id === itemId);
    if (!matchingLine) {
      return null;
    }
    const lineErrors = Object.keys(matchingLine);
    return lineErrors.includes(columnName) ? matchingLine[columnName] : null;
  };

  //Used for checking errors on the consignment modals without closing the modal
  const checkErrorsConsignmentLocal = useCallback(
    async (modal, skipLocal) => {
      let _errors = {};
      const errorToCheck = errorDefinitions['Consignments'].filter((e) => e.id === modal && e.type === 'modal')[0];
      let allPassed = true;
      for (const element of errorToCheck.children) {
        const subErr = element;
        let passed;
        if (subErr.isAsync)
          passed = await subErr.func(currentConsignment?.[(skipLocal ? '' : '_') + modal]?.[subErr.id]);
        else passed = subErr.func(currentConsignment?.[(skipLocal ? '' : '_') + modal]?.[subErr.id]);
        if (!passed) {
          allPassed = false;
          if (!_errors?.[modal]) _errors[modal] = { [subErr.id]: { msg: subErr.errorText } };
          else _errors[modal][subErr.id] = { msg: subErr.errorText };
        }
      }
      if (!allPassed) _errors[modal].modalError = { msg: errorToCheck.errorText };

      //Return the value
      if (Object.keys(_errors).length === 0) return null;

      return _errors;
    },
    [currentConsignment, currentConsignmentErrors, errorsLocal]
  );

  const checkErrorsConsignment = useCallback(
    async (consignmentToCheck) => {
      const consignmentToUse = consignmentToCheck || currentConsignment;
      let _errors = { id: consignmentToUse.id, lines: [] };
      let _errorsHMRC = { id: consignmentToUse.id, lines: [] };
      const errorsToCheck = errorDefinitions['Consignments'];
      for (const err of errorsToCheck) {
        if (err.type === 'modal') {
          let allPassed = true;
          for (const subErr of err.children) {
            let passed;
            if (subErr.isAsync) passed = await subErr.func(consignmentToUse?.[err.id]?.[subErr.id]);
            else passed = subErr.func(consignmentToUse?.[err.id]?.[subErr.id]);
            if (!passed) {
              allPassed = false;
              if (!_errors?.[err.id]) _errors[err.id] = { [subErr.id]: { msg: subErr.errorText } };
              else _errors[err.id][subErr.id] = { msg: subErr.errorText };
            }
          }
          if (!allPassed) _errors[err.id].modalError = { msg: err.errorText };
        } else if (err.type === 'consignmentLines') {
          let allPassedValidation = true;
          let allPassedHMRC = true;
          consignmentToUse.lines.forEach((line) => {
            let linePassedValidation = true;
            let linePassedHMRC = true;
            err.children.forEach((subErr) => {
              const passed = subErr.func(line?.[subErr.id], line);
              const matchingErrorLine = _errors.lines.find((l) => l.id === line.id);
              if (!passed) {
                linePassedValidation = false;
                allPassedValidation = false;
                if (!matchingErrorLine) {
                  _errors = {
                    ..._errors,
                    lines: [..._errors.lines, { id: line.id, [subErr.id]: { msg: subErr.errorText } }],
                  };
                } else matchingErrorLine[subErr.id] = { msg: subErr.errorText };
              } else {
                const HMRCError = findMatchingConsignmentError(consignmentToUse.id, line.id, subErr.id);
                const matchingErrorLine = _errorsHMRC.lines.find((l) => l.id === line.id);
                if (HMRCError) {
                  linePassedHMRC = false;
                  allPassedHMRC = false;
                  if (!matchingErrorLine) {
                    _errorsHMRC = {
                      ..._errorsHMRC,
                      lines: [
                        ..._errorsHMRC.lines,
                        {
                          id: line.id,
                          [subErr.id]: {
                            msg: 'There are errors with this field',
                            msgs: HMRCError.map((e) => e.scenario),
                          },
                        },
                      ],
                    };
                  } else matchingErrorLine[subErr.id] = { msg: HMRCError.scenario };
                }
              }
            });
            if (!linePassedValidation) {
              const matchingErrorLine = _errors.lines.find((l) => l.id === line.id);
              matchingErrorLine.lineError = { msg: err.errorTextLocal };
            }
            if (!linePassedHMRC) {
              const matchingErrorLine = _errorsHMRC.lines.find((l) => l.id === line.id);
              matchingErrorLine.lineError = { msg: err.errorTextLocal };
            }
          });
          if (!allPassedValidation) _errors.lineErrors = { msg: err.errorText };
          if (!allPassedHMRC) _errorsHMRC.lineErrors = { msg: err.errorText };
        } else if (err.id === 'consignmentLinesPresent') {
          const passed = consignmentToUse?.lines?.length > 0;
          if (!passed) _errors[err.id] = { msg: err.errorText };
        } else {
          const passed = err.func(consignmentToUse?.[err.id]);
          if (!passed) _errors[err.id] = { msg: err.errorText };
        }
      }
      if (!consignmentToCheck) {
        setCurrentConsignmentErrors(_errors);
        setCurrentConsignmentHMRCErrors(_errorsHMRC);
      } else {
        return { _errors, _errorsHMRC };
      }
    },
    [currentConsignment, errorDefinitions, referenceData]
  );

  const checkErrorsStandard = useCallback(
    async (screen, returnInsteadOfSet) => {
      const _errors = {};
      const _hmrcErrors = {};
      const errorsToCheck = errorDefinitions[screen];
      for (const err of errorsToCheck) {
        if (err.type === 'modal') {
          let allPassed = true;
          err.children.forEach((subErr) => {
            const passed = subErr.func(data?.[screen]?.[err.id]?.[subErr.id]);
            if (!passed) {
              allPassed = false;
              if (!_errors?.[err.id]) _errors[err.id] = { [subErr.id]: { msg: subErr.errorText } };
              else _errors[err.id][subErr.id] = { msg: subErr.errorText };
            }
          });
          if (!allPassed) _errors[err.id].modalError = { msg: err.errorText };
        } else if (err.type === 'date') {
          const passed = err.func(data?.[screen]?.[err.id + 'Date']);
          if (!passed) _errors[err.id] = { msg: err.errorText };
        } else if (err.type === 'dateTime') {
          const passed = err.func(data?.[screen]?.[err.id + 'DateTime'], data?.[screen]?.['departureDate']);
          if (!passed) _errors[err.id] = { msg: err.errorText };
        } else {
          let passed;
          if (err.isAsync) {
            passed = await err.func(data?.[screen]?.[err.id]);
          } else {
            passed = err.func(data?.[screen]?.[err.id]);
          }
          if (!passed) _errors[err.id] = { msg: err.errorText };
          else if (data.ErrorCodeColumnMapping?.[screen]?.[err.id]) {
            _hmrcErrors[err.id] = {
              msg: 'There are errors with a field',
              msgLocal: 'There are errors with this field',
              msgs: data.ErrorCodeColumnMapping?.[screen]?.[err.id].map((e) => e.scenario),
            };
          }
        }
      }
      if (returnInsteadOfSet) return { _errors, _hmrcErrors };
      setErrors((prev) => ({ ...prev, [screen]: _errors }));
    },
    [data, errorDefinitions]
  );

  //Call appropriate error check function based on screen
  const checkErrors = useCallback(
    async (screen) => {
      if (screen !== 'Consignments') return await checkErrorsStandard(screen);
      return await checkErrorsConsignment();
    },
    [checkErrorsStandard, checkErrorsConsignment]
  );

  //===============================================================================
  //===================END OF MAIN ERROR CHECKING FUNCTIONS========================
  //===============================================================================

  //Used to show the orange warning triangles on the tabs
  const tabValidationErrors = useMemo(
    () => ({
      ...errors,
      ...((errors.Consignments &&
        errors.Consignments.length > 0 &&
        errors.Consignments.filter((c) => Object.keys(c).filter((k) => k !== 'lines' && k !== 'id').length > 0).length >
          0) ||
      (data.Consignments?.length < 1 && consignmentsVisited)
        ? {
            Consignments: {
              modalError: {
                msg:
                  data.Consignments?.length > 0
                    ? 'There are errors with the consignments.'
                    : 'There must be at least 1 consignment.',
              },
            },
          }
        : { Consignments: {} }),
      ...(currentConsignmentErrors &&
      Object.keys(currentConsignmentErrors).filter((k) => k !== 'lines' && k !== 'id').length > 0
        ? { AddingConsignment: currentConsignmentErrors, UpdatingConsignment: currentConsignmentErrors }
        : { AddingConsignment: {}, UpdatingConsignment: {} }),
    }),
    [errors, data, currentConsignmentErrors, consignmentsVisited]
  );

  //Used to show the red warning triangles on the tabs
  const HMRCtabValidationErrors = useMemo(
    () => ({
      ...errorsHMRC,
      ...(errorsHMRC.Consignments &&
      errorsHMRC.Consignments.length > 0 &&
      errorsHMRC.Consignments.filter((c) => Object.keys(c).filter((k) => k !== 'lines' && k !== 'id').length > 0)
        .length > 0
        ? { Consignments: { modalError: { msg: 'There are HMRC errors with the consignments.' } } }
        : { Consignments: undefined }),
      ...(currentConsignmentHMRCErrors &&
      Object.keys(currentConsignmentHMRCErrors).filter((k) => k !== 'lines' && k !== 'id').length > 0
        ? { AddingConsignment: currentConsignmentHMRCErrors, UpdatingConsignment: currentConsignmentHMRCErrors }
        : { AddingConsignment: {}, UpdatingConsignment: {} }),
    }),
    [errorsHMRC, data, currentConsignmentErrors]
  );

  const hasErrors = useCallback(
    (screen) => {
      return tabValidationErrors[screen] && Object.keys(tabValidationErrors[screen]).length > 0;
    },
    [tabValidationErrors]
  );

  //Show the fix or continue modal if there are errors. Some code related to this in the main container
  const handleShowWarningModal = useCallback(
    async (curStep, gotoFunc) => {
      setGoToNextStepFunc(() => gotoFunc);
      setActiveStep(curStep);
      if (curStep === 'AddingConsignment' || curStep === 'UpdatingConsignment') {
        await checkErrors('Consignments');
        setErrorsChecked(true);
      } else if (curStep !== 'ReviewAndSubmit' && curStep !== 'Consignments' && curStep !== 'uploadConsignment') {
        await checkErrors(curStep);
        setErrorsChecked(true);
      } else setErrorsChecked(true);
    },
    [checkErrors, errorsChecked]
  );

  //Check the errors object to see if there are any errors
  const allValid = useMemo(() => {
    const passedStandard =
      Object.keys(errors)
        .filter((k) => k !== 'Consignments')
        .map((k) => errors[k])
        .filter((v) => Object.keys(v).length > 0).length === 0;
    const passedConsignment =
      errors?.Consignments?.filter?.((c) => Object.keys(c).filter((k) => k !== 'lines' && k !== 'id').length > 0)
        .length === 0;
    const dataPresent =
      data.TransportAndJourney && data.Consignments && data.AdditionalDetails && data.Consignments?.length > 0;
    return passedStandard && passedConsignment && dataPresent;
  }, [errors, data]);

  //Does the current consignment have data? Used to determine if the consignment should be saved upon leaving the page.
  const currentConsignmentHasData = useMemo(() => {
    if (!currentConsignment) return false;
    if (currentConsignment?.lines?.length > 0) return true;
    const keys = Object.keys(currentConsignment);
    if (keys.filter((k) => k !== 'lines' && k !== 'Consignee' && k !== 'Consignor').length > 0) return true;
    if (Object.keys(currentConsignment.Consignee || {}).length > 0) return true;
    return Object.keys(currentConsignment.Consignor || {}).length > 0;
  }, [currentConsignment]);

  return {
    errors,
    setErrors,
    errorsHMRC,
    setErrorsHMRC,
    checkErrors,
    checkErrorsStandard,
    checkErrorsConsignment,
    checkErrorsConsignmentLocal,
    hasErrors,
    handleShowWarningModal,
    allValid,
    isAccompanied,
    tabValidationErrors,
    HMRCtabValidationErrors,
    currentConsignmentHasData,
    errorsLocal,
    setErrorsLocal,
    initialErrorCheckRun,
  };
};

export default useErrors;
